import { IconButton, Tooltip } from "@mui/material";
import IosShareIcon from "@mui/icons-material/IosShare";
import dayjs from "../../../utils/dayjs";
import * as arrow from "apache-arrow";
import { isDateColumnBySchema } from "../../../utils/mui_table_utils";
import { MUIFlatTableColumn } from "../../../components/universal_mui_table/MUIFlatTable";
import {
  getFlatTableData,
  getPivotTableData,
  getComparisonTableData,
} from "../../../utils/mui_flat_table_data";

export function exportUniversalMUITableCSV(
  tableData: {
    columns: MUIFlatTableColumn[];
    getRow: (index: number) => Record<string, any>;
    rowCount: number;
    columnTotals?:
      | Record<string, number>
      | Array<{ tableId: string; totals: Record<string, number> }>;
  },
  title: string,
  columnSchema: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  > = {},
  showTotals: boolean = false,
  excludeDetailRows: boolean = false
) {
  const { columns, getRow, rowCount, columnTotals } = tableData;

  // Filter out expand/collapse columns and build CSV header
  const filteredColumns = columns.filter((col) => {
    // Exclude expand/collapse columns - only the "expand" key is used
    return col.key !== "expand";
  });

  const header = filteredColumns.map((col) => {
    // Map table_id to "run" for better readability
    if (col.key === "table_id") {
      return "run";
    }
    // If column key is a timestamp (epoch ms), format it as a date
    if (!isNaN(Number(col.key)) && col.key.length > 10) {
      return dayjs.utc(Number(col.key)).format("MM/DD/YY");
    }
    return col.key;
  });

  // Build CSV rows by iterating through all visible rows
  const rows: (string | number)[][] = [];

  for (let i = 0; i < rowCount; i++) {
    const rowData = getRow(i);

    // Skip detail rows if flag is set
    if (excludeDetailRows && rowData.type === "detail") {
      continue;
    }

    const row: (string | number)[] = [];

    filteredColumns.forEach((col) => {
      let value = rowData[col.key];

      // Format dates in data values (not column headers)
      if (isDateColumnBySchema(col.key, columnSchema) && value) {
        value = dayjs.utc(value).format("MM/DD/YY");
      }

      // Generic check for any 13-digit epoch timestamp - convert to MM/DD/YY
      if (
        value &&
        (typeof value === "string" || typeof value === "number") &&
        /^\d{13}$/.test(String(value))
      ) {
        const epochMs = parseInt(String(value), 10);
        if (!isNaN(epochMs)) {
          value = dayjs.utc(epochMs).format("MM/DD/YY");
        }
      }

      row.push(safeCellValue(value));
    });

    rows.push(row);
  }

  // Add totals row if requested and available
  if (showTotals && columnTotals) {
    const totalsRow: (string | number)[] = [];

    filteredColumns.forEach((col, index) => {
      let totalValue: string | number = "";

      if (Array.isArray(columnTotals)) {
        // For comparison tables, we could add multiple total rows
        // For now, let's just add the first one or skip
        const firstTotal = columnTotals[0];
        if (firstTotal && firstTotal.totals[col.key] !== undefined) {
          totalValue = firstTotal.totals[col.key];
        }
      } else {
        // For regular tables
        if (index === 0) {
          totalValue = "Total";
        } else if (columnTotals[col.key] !== undefined) {
          totalValue = columnTotals[col.key];
        }
      }

      totalsRow.push(safeCellValue(totalValue));
    });

    rows.push(totalsRow);
  }

  // Generate CSV string
  const csv = [header, ...rows]
    .map((row) =>
      row
        .map((cell) => {
          const cellStr = safeCellValue(cell);
          if (
            cellStr.includes(",") ||
            cellStr.includes('"') ||
            cellStr.includes("\n")
          ) {
            return '"' + cellStr.replace(/"/g, '""') + '"';
          }
          return cellStr;
        })
        .join(",")
    )
    .join("\r\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

  // Generate filename from title
  const sanitizedTitle =
    title && title.trim()
      ? title.replace(/[^a-z0-9]/gi, "_").toLowerCase()
      : "export";
  const filename = `${sanitizedTitle}.csv`;

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportCustomViewCSV(
  arrowTable: arrow.Table,
  viewParams: any,
  title: string,
  columnSchema: Record<
    string,
    { dtype: string; min?: string; max?: string; unique_values?: string[] }
  > = {},
  sortConfig: Array<{
    key: string;
    direction: "asc" | "desc";
    index: number;
  }> = [],
  viewControllerConfig?: any
) {
  const tableType = viewControllerConfig?.tableType || "pivot";
  const detectedTableType = tableType;
  if (!arrowTable || !viewParams) {
    console.warn("[Export] Missing arrowTable or viewParams");
    return;
  }

  // Process the data the same way UniversalTableViewer does
  let tableData;
  const columns = arrowTable.schema.fields;

  // Use custom getMatrix function if provided, otherwise fall back to default logic
  if (viewControllerConfig?.getMatrix) {
    tableData = viewControllerConfig.getMatrix({
      arrowTable,
      columns,
      viewParams,
      demandWalkConfig: viewControllerConfig.demandWalkConfig,
      purchaseOrderConfig: viewControllerConfig.purchaseOrderConfig,
      sortConfig,
      onSort: () => {}, // No-op for export
      setSortConfig: () => {}, // No-op for export
      columnSchema: columnSchema || {},
      theme: {}, // Empty theme for export
      expandedGroups: new Set(["*"]), // Use wildcard to expand all groups
      setExpandedGroups: () => {}, // No-op for export
      columnHeaderMap: {},
      firstMount: { current: false },
    });
  } else {
    if (columns.some((col) => col.name === "table_id")) {
      tableData = getComparisonTableData({
        arrowTable,
        columns,
        viewParams,
        sortConfig,
        expandedGroups: new Set(["*"]), // Use wildcard to expand all groups
        setExpandedGroups: () => {}, // No-op for export
      });
    } else if (detectedTableType === "flat") {
      tableData = getFlatTableData({
        arrowTable,
        columns,
        sortConfig,
      });
    } else {
      tableData = getPivotTableData({
        arrowTable,
        columns,
        viewParams,
        sortConfig,
      });
    }
  }

  if (!tableData) {
    console.warn("[Export] Failed to process table data");
    return;
  }

  // Use the universal export function with the processed data
  const excludeDetailRows = viewControllerConfig?.excludeDetailRows ?? false;
  exportUniversalMUITableCSV(
    tableData,
    title,
    columnSchema,
    false,
    excludeDetailRows
  );
}

function safeCellValue(val: any) {
  if (val === null || val === undefined) return "";
  if (typeof val === "bigint") return val.toLocaleString();
  if (typeof val === "object" && val instanceof Date)
    return dayjs.utc(val).format("MM/DD/YY");
  return String(val);
}

export const Download = ({
  isLoading,
  onClick,
}: {
  isLoading: boolean;
  onClick: () => void;
}) => {
  return (
    <Tooltip title="Export CSV">
      <IconButton disabled={isLoading} sx={{ mr: 1 }} onClick={onClick}>
        <IosShareIcon />
      </IconButton>
    </Tooltip>
  );
};
